/* global process */
// Primero, antes que cualquier otro import, y rompiendo a propósito el orden alfabético
// del resto: define el global `regeneratorRuntime`, que `@oneclick/react-ocviewers-component`
// viene transpilado esperando —no importa el runtime ni lo declara como dependencia—.
// Lo aportaba `react-app-polyfill/stable` (react-scripts) y rsbuild no inyecta polyfills
// por su cuenta (`output.polyfill` es 'off' por defecto).
//
// No hace falta `core-js`: `core-js-compat` confirma que el browserslist del proyecto ya
// soporta de forma nativa las APIs que usa el código (Sonar marca S7742 si se importa).
import 'regenerator-runtime/runtime';

import '@oneclick/react-lemonade-editor/dist/index.css';
import '@oneclick/react-mint-editor/dist/style.css';
import '@oneclick/tangerine-viewer/index.css';
import { MATH_RENDER_ENGINE_TANGERINE } from 'app/config/config';
import axios from 'axios';
import React from 'react';
import ReactDOM from 'react-dom';
import packageJson from '../package.json'; // Importar el package.json
import App from './App';
import * as env from './app/config/environment';
import store, { persistor } from './app/store/store';
import { isCssChunkError, writeCssChunkReloadKeyIfAbsent } from './app/utils/chunkReload';
import { isBymeDigital, isEdelvives } from './app/utils/utils';
import { markPracticeLaunchTab } from './core/chatbot/utils/practiceLaunch';
import { setupAxios } from './core/utils/axios';
import { onMigrateToPolpFinished } from './core/utils/utils';
import * as serviceWorker from './serviceWorker';

window.mathRenderEngineFroalaV3 = MATH_RENDER_ENGINE_TANGERINE;

// Intercepts CSS chunk errors before Sentry. Handles three failure modes:
// 1. Plain Event on <link rel="stylesheet"> fail (no .error property)
// 2. ErrorEvent with error.code === 'CSS_CHUNK_LOAD_FAILED'
// 3. unhandledrejection from a rejected CSS chunk import() promise
// Writes the sessionStorage key on first failure so the boot-time snapshot in
// cssChunkKeyExistedAtBoot (chunkReload.ts) correctly distinguishes first vs persistent.
globalThis.addEventListener('error', function cssChunkErrorHandler(event) {
  const target = event.target;
  // Scope to async CSS chunks only (/static/css/); excludes static stylesheets from index.html
  // (/css/styles.css, /api/organization/css/stylesheet) which are not code-split chunks.
  const isCssLinkFail =
    target?.tagName === 'LINK' &&
    target?.rel === 'stylesheet' &&
    typeof target?.href === 'string' &&
    target.href.includes('/static/css/');
  if (!isCssLinkFail && !isCssChunkError(event.error)) return;
  if (writeCssChunkReloadKeyIfAbsent()) {
    globalThis.location.reload();
  }
}, true);

globalThis.addEventListener('unhandledrejection', function cssChunkRejectionHandler(event) {
  if (!isCssChunkError(event.reason)) return;
  if (writeCssChunkReloadKeyIfAbsent()) {
    globalThis.location.reload();
  }
});

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;
/**
 * Inject Frontoffice interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */

const { API_URL } = env[process.env.REACT_APP_ENV] || env['defaultConfig'];
setupAxios(axios, API_URL, store);

// Antes de montar React: el proveedor del chat decide en su primer render si la pestaña está
// aislada de las de estudio (TANAI-2622), y esta marca es la que se lo dice.
markPracticeLaunchTab(window.location, PUBLIC_URL);

/**
 * Add langs resources from translation folder
 */

// Object.keys(resources).forEach(function (lang) {
//   resources[lang].map((langScope) => {
//     Object.keys(langScope).forEach(function (langScopeNs) {
//       i18next.addResources(lang, langScopeNs, langScope[langScopeNs]);
//     });
//   });
// });

/**
 * Initialize and render the application.
 * Loads the appropriate styles based on the client before rendering.
 */
async function initializeApp() {
  try {
    // Cargar estilos según el cliente
    if (isEdelvives()) {
      await import('./edelvives-index.scss');
      document.body.classList.add(`client--EDELVIVES_DIGITAL_PLUS`);
    } else {
      await import('./index.scss');
    }

    // Ñapa aceptada, ver `isBymeDigital()` en `app/utils/utils`
    if (isBymeDigital()) {
      document.documentElement.style.setProperty('--landing', "url('/images/byme/login.jpg')");
    }
  } catch (error) {
    console.error('Error loading styles:', error);
    // Cargar estilos por defecto en caso de error
    await import('./index.scss');
  }

  // Renderizar la aplicación solo después de que los estilos estén cargados
  // eslint-disable-next-line react/no-deprecated -- el proyecto está en React 17, donde `createRoot` no existe todavía
  ReactDOM.render(<App store={store} persistor={persistor} basename={PUBLIC_URL} />, document.getElementById('root'));

  console.table([{ name: process.env.REACT_APP_WEBSITE_NAME, version: process.env.REACT_APP_VERSION, date: packageJson.date }]);

  // Si es Edelvives, añadir el event listener para la migración a Polp
  if (isEdelvives()) {
    window.addEventListener('isSignedIn', () => onMigrateToPolpFinished(store), false);
  }
}

// Inicializar la aplicación
initializeApp();

// Si es Edelvives, añadir el event listener para la migración a Polp
if (isEdelvives()) {
  window.addEventListener('isSignedIn', () => onMigrateToPolpFinished(store), false);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
